import React from 'react';
import { useMeasure } from 'react-use';
import { PluginContextProvider } from '@grafana/data';
import { getAppEvents } from '@grafana/runtime';
import { PanelChrome, ErrorBoundaryAlert, PanelContextProvider } from '@grafana/ui';
import { sceneGraph } from '../../core/sceneGraph/index.js';
import { isSceneObject } from '../../core/types.js';

function VizPanelRenderer({ model }) {
  const {
    title,
    description,
    options,
    fieldConfig,
    pluginLoadError,
    $data,
    displayMode,
    hoverHeader,
    menu,
    headerActions
  } = model.useState();
  const [ref, { width, height }] = useMeasure();
  const plugin = model.getPlugin();
  const { dragClass, dragClassCancel } = getDragClasses(model);
  const dataObject = sceneGraph.getData(model);
  const rawData = dataObject.useState();
  const dataWithFieldConfig = model.applyFieldConfig(rawData.data);
  const titleInterpolated = model.interpolate(title, void 0, "text");
  const timeZone = sceneGraph.getTimeRange(model).getTimeZone();
  if (!plugin) {
    return /* @__PURE__ */ React.createElement("div", null, "Loading plugin panel...");
  }
  if (!plugin.panel) {
    return /* @__PURE__ */ React.createElement("div", null, "Panel plugin has no panel component");
  }
  const PanelComponent = plugin.panel;
  if ($data && $data.setContainerWidth) {
    $data.setContainerWidth(width);
  }
  const titleItems = [];
  if (model.state.$timeRange) {
    titleItems.push(/* @__PURE__ */ React.createElement(model.state.$timeRange.Component, {
      model: model.state.$timeRange,
      key: model.state.key
    }));
  }
  let panelMenu;
  if (menu) {
    panelMenu = /* @__PURE__ */ React.createElement(menu.Component, {
      model: menu
    });
  }
  let actionsElement;
  if (headerActions) {
    if (Array.isArray(headerActions)) {
      actionsElement = /* @__PURE__ */ React.createElement(React.Fragment, null, headerActions.map((action) => {
        return /* @__PURE__ */ React.createElement(action.Component, {
          model: action,
          key: `${action.state.key}`
        });
      }));
    } else if (isSceneObject(headerActions)) {
      actionsElement = /* @__PURE__ */ React.createElement(headerActions.Component, {
        model: headerActions
      });
    } else {
      actionsElement = headerActions;
    }
  }
  const data = dataWithFieldConfig;
  const isReadyToRender = dataObject.isDataReadyToDisplay ? dataObject.isDataReadyToDisplay() : true;
  return /* @__PURE__ */ React.createElement("div", {
    ref,
    style: { position: "absolute", width: "100%", height: "100%" }
  }, width > 0 && height > 0 && /* @__PURE__ */ React.createElement(PanelChrome, {
    title: titleInterpolated,
    description: description ? () => model.interpolate(description) : "",
    loadingState: data.state,
    statusMessage: getChromeStatusMessage(data, pluginLoadError),
    width,
    height,
    displayMode,
    hoverHeader,
    titleItems,
    dragClass,
    actions: actionsElement,
    dragClassCancel,
    padding: plugin.noPadding ? "none" : "md",
    menu: panelMenu,
    onCancelQuery: model.onCancelQuery
  }, (innerWidth, innerHeight) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ErrorBoundaryAlert, {
    dependencies: [plugin, data]
  }, /* @__PURE__ */ React.createElement(PluginContextProvider, {
    meta: plugin.meta
  }, /* @__PURE__ */ React.createElement(PanelContextProvider, {
    value: model.getPanelContext()
  }, isReadyToRender && /* @__PURE__ */ React.createElement(PanelComponent, {
    id: 1,
    data,
    title,
    timeRange: data.timeRange,
    timeZone,
    options,
    fieldConfig,
    transparent: false,
    width: innerWidth,
    height: innerHeight,
    renderCounter: 0,
    replaceVariables: model.interpolate,
    onOptionsChange: model.onOptionsChange,
    onFieldConfigChange: model.onFieldConfigChange,
    onChangeTimeRange: model.onTimeRangeChange,
    eventBus: getAppEvents()
  })))))));
}
function getDragClasses(panel) {
  var _a, _b;
  const parentLayout = sceneGraph.getLayout(panel);
  const isDraggable = parentLayout == null ? void 0 : parentLayout.isDraggable();
  if (!parentLayout || !isDraggable || itemDraggingDisabled(panel, parentLayout)) {
    return { dragClass: "", dragClassCancel: "" };
  }
  return { dragClass: (_a = parentLayout.getDragClass) == null ? void 0 : _a.call(parentLayout), dragClassCancel: (_b = parentLayout == null ? void 0 : parentLayout.getDragClassCancel) == null ? void 0 : _b.call(parentLayout) };
}
function itemDraggingDisabled(item, layout) {
  let ancestor = item.parent;
  while (ancestor && ancestor !== layout) {
    if ("isDraggable" in ancestor.state && ancestor.state.isDraggable === false) {
      return true;
    }
    ancestor = ancestor.parent;
  }
  return false;
}
function getChromeStatusMessage(data, pluginLoadingError) {
  if (pluginLoadingError) {
    return pluginLoadingError;
  }
  let message = data.error ? data.error.message : void 0;
  if (data.errors) {
    message = data.errors.map((e) => e.message).join(", ");
  }
  return message;
}

export { VizPanelRenderer };
//# sourceMappingURL=VizPanelRenderer.js.map
